import React from 'react';
import { Button } from 'semantic-ui-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

interface ActionResultMessageProps {
  message: string;
  actionResult?: {
    action: string;
    success: boolean;
    navigation_url?: string;
    data?: any;
  };
}

const ActionResultMessage: React.FC<ActionResultMessageProps> = ({ message, actionResult }) => {
  const navigate = useNavigate();

  const handleNavigate = () => {
    if (actionResult?.navigation_url) {
      navigate(actionResult.navigation_url);
    }
  };

  if (!actionResult) {
    return <MessageContainer>{message}</MessageContainer>;
  }

  // Format data for display
  const formatData = (data: any): string => {
    if (!data) return '';

    if (Array.isArray(data)) {
      // List of items
      if (data.length === 0) return 'No items found.';
      return data
        .map((item: any) => {
          if (typeof item === 'object' && item.name) {
            return `• ${item.name}${item.description ? ` - ${item.description}` : ''}`;
          }
          return `• ${JSON.stringify(item)}`;
        })
        .join('\n');
    }

    if (typeof data === 'object') {
      // Single item or object
      const parts: string[] = [];
      if (data.name) parts.push(`Name: ${data.name}`);
      if (data.description) parts.push(`Description: ${data.description}`);
      if (data.value !== undefined) parts.push(`Value: ${data.value}`);
      if (parts.length === 0) {
        // Fallback: show all non-id fields
        Object.entries(data).forEach(([key, value]) => {
          if (key !== 'id' && key !== 'organization_id' && value !== null && value !== undefined) {
            parts.push(`${key}: ${value}`);
          }
        });
      }
      return parts.join('\n');
    }

    return String(data);
  };

  const dataDisplay = actionResult.data ? formatData(actionResult.data) : null;

  return (
    <ActionContainer>
      <MessageContainer $success={actionResult.success}>
        {message}
        {dataDisplay && (
          <DataDisplay>
            {dataDisplay.split('\n').map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </DataDisplay>
        )}
      </MessageContainer>
      {actionResult.navigation_url && (
        <ActionButton onClick={handleNavigate} size="small" primary>
          View Details
        </ActionButton>
      )}
    </ActionContainer>
  );
};

export default ActionResultMessage;

const ActionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MessageContainer = styled.div<{ $success?: boolean }>`
  color: ${props => (props.$success === false ? '#ff4444' : '#2a3039')};
  font-weight: ${props => (props.$success === false ? 'normal' : 'normal')};
`;

const DataDisplay = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e0e0e0;
  font-size: 13px;
  line-height: 1.6;
  color: #555;

  div {
    margin: 2px 0;
  }
`;

const ActionButton = styled(Button as any)`
  align-self: flex-start;
  margin-top: 4px;
`;
